import { Colors } from '../../theme'

// Marcador de la moto. Normalmente es el marcador avanzado de Google: su contenido es HTML
// y se anima con CSS (style/map.css). Ese marcador exige un Map ID válido; si Google lo
// rechaza (por ejemplo, si limita el DEMO_MAP_ID), se usa el marcador clásico para que la
// moto nunca desaparezca: se desliza y rebota, pero sin el salto ni la onda al llegar.
const ARRIVING_CLASS = 'is-arriving'
const SELECTED_CLASS = 'is-selected'

// La imagen mide 216x233 y su punta inferior es la posición exacta: se conserva la proporción.
const ICON_SIZE = { width: 50, height: 54 }

const isClassic = (marker) => typeof marker.getPosition === 'function'

const classicIcon = (img) => ({
  url: img,
  scaledSize: new window.google.maps.Size(ICON_SIZE.width, ICON_SIZE.height),
})

export const advancedMarkersAvailable = () =>
  Boolean(window.google?.maps?.marker?.AdvancedMarkerElement)

const createClassicMarker = ({
  map, position, img, selected, onClick,
}) => {
  const marker = new window.google.maps.Marker({
    map,
    position,
    icon: classicIcon(img),
    animation: selected ? window.google.maps.Animation.BOUNCE : null,
  })
  if (onClick) marker.addListener('click', onClick)
  return marker
}

const createAdvancedMarker = ({
  map, position, img, selected, onClick,
}) => {
  const content = document.createElement('div')
  content.className = 'trakku-marker'
  content.classList.toggle(SELECTED_CLASS, Boolean(selected))
  content.style.setProperty('--trakku-ripple', Colors.TEXT_CIAN)
  // Dos capas: el rebote continuo va en la capa externa y el salto de llegada en el ícono.
  const bounce = document.createElement('span')
  bounce.className = 'trakku-marker__bounce'
  const icon = document.createElement('img')
  icon.src = img
  icon.alt = ''
  icon.draggable = false
  bounce.appendChild(icon)
  content.appendChild(bounce)
  // Al terminar la onda se quita la llegada y la moto vuelve a rebotar.
  content.addEventListener('animationend', (event) => {
    if (event.animationName === 'trakku-ripple') content.classList.remove(ARRIVING_CLASS)
  })

  const marker = new window.google.maps.marker.AdvancedMarkerElement({
    map,
    position,
    content,
    gmpClickable: Boolean(onClick),
  })
  if (onClick) marker.addListener('click', onClick)
  return marker
}

export const createVehicleMarker = ({ useAdvanced, ...options }) =>
  (useAdvanced && advancedMarkersAvailable()
    ? createAdvancedMarker(options)
    : createClassicMarker(options))

export const getMarkerPosition = (marker) => {
  const position = isClassic(marker) ? marker.getPosition() : marker.position
  if (!position) return null
  return typeof position.lat === 'function'
    ? { lat: position.lat(), lng: position.lng() }
    : { lat: position.lat, lng: position.lng }
}

export const setMarkerPosition = (marker, position) => {
  if (isClassic(marker)) {
    marker.setPosition(position)
    return
  }
  // eslint-disable-next-line no-param-reassign
  marker.position = position
}

// La moto elegida rebota sin parar, como el rebote original de Google.
export const setMarkerSelected = (marker, selected) => {
  if (isClassic(marker)) {
    // Solo si cambió: reiniciar el rebote en cada coordenada se veía como un salto.
    const animation = selected ? window.google.maps.Animation.BOUNCE : null
    if (marker.getAnimation() !== animation) marker.setAnimation(animation)
    return
  }
  marker.content?.classList.toggle(SELECTED_CLASS, Boolean(selected))
}

// Solo cambia la imagen si cambió (encendida/apagada), para que no parpadee.
export const setMarkerIcon = (marker, img) => {
  if (isClassic(marker)) {
    if (marker.getIcon()?.url !== img) marker.setIcon(classicIcon(img))
    return
  }
  const icon = marker.content?.querySelector('img')
  if (icon && icon.getAttribute('src') !== img) icon.setAttribute('src', img)
}

export const removeMarker = (marker) => {
  if (isClassic(marker)) {
    marker.setMap(null)
    return
  }
  // eslint-disable-next-line no-param-reassign
  marker.map = null
}

// Saltito con rebote y onda en el piso al llegar a la nueva coordenada (solo marcador avanzado).
export const playArrival = (marker) => {
  if (isClassic(marker)) return
  const element = marker.content
  if (!element) return
  element.classList.remove(ARRIVING_CLASS)
  // Forzar el reflow reinicia la animación si llega otra coordenada antes de terminar.
  element.getBoundingClientRect()
  element.classList.add(ARRIVING_CLASS)
}
